import {
  AnalysisFeedbackPayload,
  ChangeReasonBanner,
  ConfidenceIndicator,
  CriterionFreeText,
  EvidenceCardConfig,
} from "@risalabs_frontend_org/oasis-ui-kit";
import React from "react";

import { QuestionForUI } from "../../data-model/cmmMedicalNeccessityQuestionnaire";

import type { QuestionPatch } from "./index";
import UserFeedBackOnAnswerChange from "./userFeedbackForAnswerChange";

export interface SubjectiveQuestionProps {
  question: QuestionForUI;
  stepNumber: number;
  onChange: (patch: QuestionPatch) => void;
  disabled?: boolean;
  evidenceCards?: EvidenceCardConfig[];
  confidenceScore?: number;
  answerChangedReason?: string;
  shouldShowUserFeedBack?: boolean;
  onSubmit?: (
    payload: AnalysisFeedbackPayload & { questionName: string },
  ) => void;
  onCancel?: (payload: { questionName: string }) => void;
}

/**
 * Renders a `subjective` (free-text only) question. The textarea is bound
 * directly to `question.answer`, since these questions have no choices and
 * therefore no `free_text_answer` payload.
 */
export const SubjectiveQuestion: React.FC<SubjectiveQuestionProps> = ({
  question,
  stepNumber,
  onChange,
  disabled,
  evidenceCards,
  confidenceScore,
  answerChangedReason,
  shouldShowUserFeedBack = false,
  onSubmit,
  onCancel,
}) => {
  const handleSubmit = (payload: AnalysisFeedbackPayload) => {
    onSubmit?.({ ...payload, questionName: question.name });
  };
  const handleCancel = () => {
    onCancel?.({ questionName: question.name });
  };
  const answerValue =
    typeof question.answer === "string" ? question.answer : "";

  const shouldShowChangeReasonBanner =
    answerChangedReason && answerChangedReason.trim().length > 0;
  return (
    <CriterionFreeText
      id={question.name}
      stepNumber={stepNumber}
      title={question.question}
      textareaName={question.name}
      textareaValue={answerValue}
      textareaDisabled={disabled}
      onTextChange={(value) => onChange({ name: question.name, answer: value })}
      evidenceCards={evidenceCards}
      badge={
        <ConfidenceIndicator label="CONFIDENCE" value={confidenceScore ?? 0} />
      }
      headerSlot={
        shouldShowChangeReasonBanner ? (
          <ChangeReasonBanner
            label="ANSWER CHANGED REASON"
            reason={answerChangedReason}
            multiline={true}
          />
        ) : null
      }
      children={
        shouldShowUserFeedBack ? (
          <UserFeedBackOnAnswerChange
            onSubmit={handleSubmit}
            onCancel={handleCancel}
          />
        ) : null
      }
    />
  );
};

export default SubjectiveQuestion;
